import { ReactNode } from "react";
import { SunriseIcon, SunIcon, MoonIcon } from "@/components/common/icons";
import { GREY } from "@/config/mui/colorPalette";
import { TimeOfDay, WaitlistSource } from "@/types";

export type TimeOfDayOption = {
  id: TimeOfDay;
  label: string;
  value: TimeOfDay;
  icon: ReactNode;
  tooltip: string;
};

const createTimeOfDayData = (
  size?: number
): Record<TimeOfDay, TimeOfDayOption> => ({
  [TimeOfDay.MORNING]: {
    id: TimeOfDay.MORNING,
    value: TimeOfDay.MORNING,
    label: "Morning",
    icon: <SunriseIcon color={GREY[60]} size={size} />,
    tooltip: "Morning (6am - 12pm)",
  },
  [TimeOfDay.AFTERNOON]: {
    id: TimeOfDay.AFTERNOON,
    value: TimeOfDay.AFTERNOON,
    label: "Afternoon",
    icon: <SunIcon color={GREY[60]} size={size} />,
    tooltip: "Afternoon (12pm - 6pm)",
  },
  [TimeOfDay.EVENING]: {
    id: TimeOfDay.EVENING,
    value: TimeOfDay.EVENING,
    label: "Evening",
    icon: <MoonIcon color={GREY[60]} size={size} />,
    tooltip: "Evening (6pm - 12am)",
  },
});

export const TIME_OF_DAY_DATA = createTimeOfDayData();
export const timeOfDayOptions = Object.values(TIME_OF_DAY_DATA);
export const timeOfDayFormOptions = Object.values(createTimeOfDayData(24));
export const timeOfDayTableData = Object.fromEntries(
  Object.entries(TIME_OF_DAY_DATA).map(([key, value]) => [
    key,
    { icon: value.icon, tooltip: value.tooltip },
  ])
) as Record<TimeOfDay, { icon: ReactNode; tooltip: string }>;

export const sourceOptions = [
  {
    label: "Moxie Suite",
    value: WaitlistSource.MOXIE_SUITE,
  },
  {
    label: "Online booking",
    value: WaitlistSource.ONLINE_BOOKING,
  },
];

export const sortTimeOfDay = (times: TimeOfDay[]): TimeOfDay[] => {
  const order = timeOfDayOptions.map((option) => option.id);

  return [...times].sort((a, b) => order.indexOf(a) - order.indexOf(b));
};

export const findTimeOfDayOption = (time: TimeOfDay) =>
  timeOfDayOptions.find((opt) => opt.id === time);

export const formatTimeOfDay = (timeOfDay: TimeOfDay[]): string => {
  return sortTimeOfDay(timeOfDay)
    .map((time) => TIME_OF_DAY_DATA[time].label)
    .join(", ");
};

export const handleToggleTimeOfDay = (
  timeOfDay: TimeOfDay,
  currentTimeOfDay: TimeOfDay[]
): TimeOfDay[] => {
  if (currentTimeOfDay.includes(timeOfDay)) {
    return currentTimeOfDay.filter((time) => time !== timeOfDay);
  } else {
    return [...currentTimeOfDay, timeOfDay];
  }
};
